import { useEffect } from 'react'
import { isClientDevEnv } from '@/client-env'
import { markUpdatePending } from '@/modules/pendingUpdate'

const POLL_MS = 3 * 60 * 1000

// Emergency kill switch. When a newer deploy is detected we flip a pending
// flag; the router upgrades the user's next cross-page navigation into a full
// document load so they silently land on the fresh build (see the
// onBeforeNavigate handler in main.tsx). No banner, no reload loop — the
// reload is cache-busting because versionedEntryPlugin stamps ?dt=<deploy tag>
// on the entry-script URL, so the fresh HTML forces a fresh bundle fetch.
const VERSION_CHECK_ENABLED = true

// A real deploy tag looks like "ee9df4-33496d-28_06-10_04" (sha-suffix-date).
// We deliberately DON'T pin that exact shape (the date layout could change) —
// we just require a non-empty string of tag-safe characters. That rejects the
// values we actually want to guard against (empty, HTML from a bad
// /version.json, whitespace, JSON blobs) without being brittle to format
// changes, so a broken version.json never triggers the banner.
const DEPLOY_TAG_RE = /^[A-Za-z0-9._-]{1,100}$/

export function looksLikeDeployTag(tag: unknown): tag is string {
  return typeof tag === 'string' && DEPLOY_TAG_RE.test(tag)
}

/** A new version exists only when both tags are valid deploy tags and differ. */
export function isNewerVersion(
  currentTag: string | undefined,
  latestTag: string | undefined
): boolean {
  if (!looksLikeDeployTag(currentTag)) return false
  if (!looksLikeDeployTag(latestTag)) return false
  return currentTag !== latestTag
}

async function fetchLatestTag(): Promise<string | undefined> {
  try {
    const res = await fetch('/version.json', { cache: 'no-store' })
    if (!res.ok) return undefined
    const data: { tag?: string } = await res.json()
    return data.tag
  } catch {
    return undefined
  }
}

/**
 * Poll /version.json on an interval and on tab refocus; mark an update pending
 * when the deployed tag no longer matches the running build's baked-in
 * VITE_DEPLOYMENT_TAG (inlined by Vite at build time). No-op in dev.
 */
export function useVersionCheck(): void {
  useEffect(() => {
    if (!VERSION_CHECK_ENABLED) return
    if (isClientDevEnv) return
    const currentTag = import.meta.env.VITE_DEPLOYMENT_TAG
    let cancelled = false

    const check = async () => {
      const latest = await fetchLatestTag()
      if (cancelled) return
      if (looksLikeDeployTag(latest) && isNewerVersion(currentTag, latest)) {
        markUpdatePending()
      }
    }

    void check()
    const id = window.setInterval(check, POLL_MS)
    const onVisible = () => {
      if (document.visibilityState === 'visible') void check()
    }
    document.addEventListener('visibilitychange', onVisible)

    return () => {
      cancelled = true
      window.clearInterval(id)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [])
}
